/* External */
import getDifferenceInDays from "date-fns/differenceInDays";
import { format, setSeconds } from "date-fns";

/* Others */
import { getFormattedDuration } from "./dates";
import { RESOURCE_TYPES_ORBIS } from "./misc";

const DEFAULT_SEPARATOR = " • ";

/**
 * Join elements of array with a separator else uses a default separator.
 * @param {Array} arr Elements to join
 * @param {string} separator
 */
export const joinArrayWithSeparator = (arr = [], separator = DEFAULT_SEPARATOR) =>
  arr.filter((value) => !!value).join(separator);

/**
 * @description Method that formats season and episode
 * @method formatSeasonAndEpisode
 * @param season
 * @param episode
 */
export function formatSeasonAndEpisode(seasonStr, episodeStr) {
  const season = parseInt(seasonStr, 10);
  const episode = parseInt(episodeStr, 10);
  const formattedSeason = season ? `T${season} ` : "";
  const formattedEpisode = episode ? `E${episode}` : "";

  return formattedEpisode ? `${formattedSeason}${formattedEpisode}` : "";
}

/**
 * @method getCardTitle
 * @description Receives data of an asset and returns a card title
 */
export function getCardTitle(assetData) {
  const { resourceType = "", name = "", showName = "" } = assetData;

  const title =
    resourceType === RESOURCE_TYPES_ORBIS.EPISODES ||
    resourceType === RESOURCE_TYPES_ORBIS.SHOW_EPISODE ||
    resourceType === RESOURCE_TYPES_ORBIS.VOD_EPISODE ||
    resourceType === RESOURCE_TYPES_ORBIS.VOD_EPISODES ||
    resourceType === RESOURCE_TYPES_ORBIS.EPISODES
      ? showName
      : name;

  return title;
}

/**
 * @description Return the subtitle for a live show
 * @param {object} schedule
 */
export const getLiveShowSubtitle = (schedule = {}) => {
  const { episode = 0, genres = [], name = "", season = 0, showName = "" } = schedule;

  if (showName) {
    return joinArrayWithSeparator([formatSeasonAndEpisode(season, episode), name]);
  }

  const genre = genres && genres.length && genres[0] ? genres[0].name : "";
  return genre;
};

/**
 * @description Return the metadata for a live show
 * @param {object} schedule
 */
export const getLiveShowMetadata = (schedule) => {
  const { blackout = false, endTime = "", name = "", showName = "", startTime = "" } = schedule;
  const start = setSeconds(new Date(startTime), 0);
  const end = setSeconds(new Date(endTime), 0);

  return {
    blackout,
    title: showName ? showName : name,
    subtitle: getLiveShowSubtitle(schedule),
    timeLine: `${format(start, "HH:mm")} - ${format(end, "HH:mm")}`
  };
};

/**
 * @description Return the subtitle for a series
 * @param {string} episodeName
 * @param {number|string} episodeNumber
 * @param {number|string} seasonNumber
 */
export const getSerieSubtitle = (episodeName, episodeNumber, seasonNumber) =>
  joinArrayWithSeparator([formatSeasonAndEpisode(seasonNumber, episodeNumber), episodeName]);

export const getSerieExtraInfo = (serie) => {
  const seasons = serie?.seasonsList || [];
  const firstSeason = seasons[0];
  const lastSeason = seasons[seasons.length - 1];
  const releaseYear = firstSeason?.releaseYear;
  const finaleYear = lastSeason?.releaseYear;

  const yearsOnAir =
    releaseYear && finaleYear ? (releaseYear === finaleYear ? releaseYear : `${releaseYear} - ${finaleYear}`) : null;

  const genre = serie?.genres?.[0]?.name;

  return joinArrayWithSeparator([yearsOnAir, genre]);
};

/**
 * Return startTime text related to various (but not all) cases in PDP status text
 * @param {function} formatMessage - hook from useIntl
 * @param {date} startDate
 * @returns {string} localized startTime text for future live assets, ie: Hoy 12:00
 */
export const getAssetStartDateTime = (formatMessage, startDate) => {
  const currentTime = new Date();
  const differenceInDays = getDifferenceInDays(startDate, currentTime);

  if (differenceInDays >= 2) return format(startDate, "EEEE H:mm");
  if (differenceInDays === 1) {
    return formatMessage(
      {
        id: "remainingTimeTag.tomorrow",
        defaultMessage: "Mañana {date}"
      },
      {
        date: format(startDate, "H:mm")
      }
    );
  }
  return formatMessage(
    {
      id: "remainingTimeTag.today",
      defaultMessage: "Hoy {date}"
    },
    {
      date: format(startDate, "H:mm")
    }
  );
};

/**
 * Return startTime text related to various (but not all) cases in PDP status text
 * @param {function} formatMessage - hook from useIntl
 * @param {date} startDate
 * @param {string} channelsName
 * @returns {string} localized live PDP terciary text, ie: Hoy 12:00 · A&E
 */
export const getLiveAssetExtraInfo = (formatMessage, startDate, channelName) => {
  return joinArrayWithSeparator([getAssetStartDateTime(formatMessage, startDate), channelName]);
};

export const getVodExtraInfo = (movie, hideDuration) => {
  const { duration, realeaseYear, releaseYear, genres } = movie;
  const genre = genres?.[0]?.name;

  return joinArrayWithSeparator([realeaseYear || releaseYear, !hideDuration && getFormattedDuration(duration), genre]);
};

/**
 * @method checkAssetLabelEntitlements
 * @description Checks whether the asset's labels are present in the currently valid entitlements.
 * @param {array} assetLabels
 * @param {array} entitlementsLabels
 * @returns {boolean} - true if it finds one or more labels shared in both arrays
 */
export function checkAssetLabelEntitlements(assetLabels = [], entitlementsLabels = []) {
  if (!(assetLabels && assetLabels.length)) return true;
  const assetLabelStringArray = assetLabels.map((element) => String(element));
  return !!assetLabelStringArray.filter((label) => entitlementsLabels.includes(label)).length;
}

export const isLiveByResourceType = (resourceType) => resourceType.includes("epg");
export const isMovieByResourceType = (resourceType) => resourceType === RESOURCE_TYPES_ORBIS.VOD_MOVIES;

export const isSerieByResourceType = (resourceType) =>
  [RESOURCE_TYPES_ORBIS.SHOWS, RESOURCE_TYPES_ORBIS.EPISODES, RESOURCE_TYPES_ORBIS.VOD_EPISODES].includes(resourceType);
